import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useUserStore } from '../stores/userStore';
import { useActivityLogStore } from '../stores/activityLogStore';
import { saveAuthCredentials, getStoredAuth, clearStoredAuth } from '../utils/auth';
import type { User } from '../types/user';

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string, remember?: boolean) => Promise<void>;
  signup: (firstName: string, lastName: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  updateProfile: (data: Partial<User>) => Promise<void>;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser: setStoreUser, validateCredentials, registerUser, findUserByEmail } = useUserStore();
  const { addLog } = useActivityLogStore();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const storedAuth = getStoredAuth();
        if (storedAuth) {
          try {
            await login(storedAuth.email, storedAuth.password, true);
          } catch (error) {
            console.error('Auto-login failed:', error);
            clearStoredAuth();
          }
        }
      } catch (error) {
        console.error('Auth check error:', error);
      } finally {
        // Ensure isLoading is set to false even if there's an error
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const handlePostAuth = async () => {
    try {
      // Check for returnUrl in URL parameters
      const params = new URLSearchParams(location.search);
      const returnUrl = params.get('returnUrl');
      
      if (returnUrl) {
        // Navigate to the return URL
        navigate(returnUrl);
      } else {
        // Check for plan selection in URL parameters
        const planId = params.get('plan');
        
        if (planId && planId !== 'free') {
          // Redirect to pricing page for paid plans
          navigate('/dashboard/precos');
        } else {
          // For free plan or no plan selected, go to dashboard
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Post-auth navigation error:', error);
      // Fallback to dashboard if there's any error
      navigate('/dashboard');
    }
  };

  const login = async (email: string, password: string, remember: boolean = false) => {
    try {
      setIsLoading(true);
      
      const validatedUser = validateCredentials(email, password);
      
      if (!validatedUser) {
        throw new Error('Email ou senha inválidos');
      }

      setUser(validatedUser);
      setStoreUser(validatedUser);
      
      addLog({
        userId: validatedUser.id,
        userName: validatedUser.name,
        action: 'login',
        details: 'User logged in successfully',
        metadata: {
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        }
      });

      if (remember) {
        saveAuthCredentials(email, password, true);
      }

      await handlePostAuth();
      toast.success('Login realizado com sucesso!');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao fazer login');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (firstName: string, lastName: string, email: string, password: string) => {
    try {
      setIsLoading(true);
      
      const existingUser = findUserByEmail(email);
      if (existingUser) {
        throw new Error('Este email já está cadastrado');
      }

      const newUser = {
        id: crypto.randomUUID(),
        name: `${firstName} ${lastName}`,
        email,
        password,
        role: 'user' as const,
        isNewUser: true,
        status: 'active',
        createdAt: new Date().toISOString()
      };

      registerUser(newUser);
      setUser(newUser);
      setStoreUser(newUser);

      addLog({
        userId: newUser.id,
        userName: newUser.name,
        action: 'signup',
        details: `New user registration - ${firstName} ${lastName}`,
        metadata: {
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        }
      });

      saveAuthCredentials(email, password, true);
      
      // Get plan parameter from URL if it exists
      const params = new URLSearchParams(location.search);
      const planParam = params.get('plan');
      
      if (planParam && planParam !== 'free') {
        // If signing up with a plan, redirect to success page
        navigate(`/success?plan=${planParam}`);
      } else {
        // Otherwise go to dashboard
        navigate('/dashboard');
      }
      
      toast.success('Conta criada com sucesso!');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao criar conta');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    if (user) {
      addLog({
        userId: user.id,
        userName: user.name,
        action: 'logout',
        details: 'User logged out',
        metadata: {
          timestamp: new Date().toISOString()
        }
      });
    }
    
    clearStoredAuth();
    setUser(null);
    setStoreUser(null);
    navigate('/login');
    toast.success('Logout realizado com sucesso!');
  };

  const updateProfile = async (data: Partial<User>) => {
    if (!user) return;

    try {
      const updatedUser = { ...user, ...data };
      setUser(updatedUser);
      setStoreUser(updatedUser);
      
      addLog({
        userId: user.id,
        userName: user.name,
        action: 'settings_change',
        details: 'User updated profile settings',
        metadata: {
          changes: data,
          timestamp: new Date().toISOString()
        }
      });

      toast.success('Perfil atualizado com sucesso!');
    } catch (error) {
      toast.error('Erro ao atualizar perfil');
      throw error;
    }
  };

  const updatePassword = async (currentPassword: string, newPassword: string) => {
    if (!user) return;

    try {
      // Validate current password
      const isValid = validateCredentials(user.email, currentPassword);
      if (!isValid) {
        throw new Error('Senha atual incorreta');
      }

      // Update password in user store
      const updatedUser = { ...user, password: newPassword };
      setUser(updatedUser);
      setStoreUser(updatedUser);

      addLog({
        userId: user.id,
        userName: user.name,
        action: 'settings_change',
        details: 'User changed password',
        metadata: {
          timestamp: new Date().toISOString()
        }
      });

      // Update stored credentials if "remember me" is active
      const remembered = localStorage.getItem('kontty_remember') === 'true';
      if (remembered) {
        saveAuthCredentials(user.email, newPassword, true);
      }

      toast.success('Senha atualizada com sucesso!');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar senha');
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user,
      login,
      signup,
      logout,
      isLoading,
      updateProfile,
      updatePassword
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}